'use client';
import { GroqImage } from '@/helpers/Image';
import { GROQButton, GROQImage } from '@/sanity/lib/definitions';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { PortableText } from 'next-sanity';
import { PortableTextBlock } from 'sanity';
import { motion } from 'framer-motion';
import { CTAButton } from '@/shadcn/ui/button';

interface IDiscover {
  label: string;
  title: {
    lineOne: string;
    lineTwo: string;
    lineThree: string;
  };
  description: PortableTextBlock[];
  webButton: GROQButton;
  image: GROQImage;
}

export default function Discover({
  label,
  title,
  description,
  webButton,
  image,
}: IDiscover) {
  return (
    <section id="discover" className="py-[12rem] lg:py-[24.75rem]">
      <div className="container max-w-[200rem] flex flex-col items-end gap-[7rem] lg:gap-[15rem]">
        <motion.div
          initial={{ clipPath: 'inset(0% 0% 100% 0%)' }}
          whileInView={{ clipPath: 'inset(0% 0% 0% 0%)' }}
          transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
          className="flex lg:flex-row flex-col w-full justify-between items-start lg:items-end gap-[3rem] lg:gap-[18rem]"
        >
          <motion.div className="flex lg:w-[142rem]  flex-col gap-[3.62rem] uppercase text-[#dadada]">
            <p className="font-nunito text-[1.625rem] font-[300] tracking-[0.53625rem] flex items-center gap-[2rem] leading-[100%]">
              <span className="block h-[.125rem] bg-offwhite w-[15rem] lg:w-[18rem]"></span>
              {label}
            </p>
            <h2 className="text-[6rem] lg:text-[9rem] lg:leading-[120%] leading-[100%] flex flex-col">
              <span className="block lg:text-[6rem] text-[3rem]">
                {title.lineOne}
              </span>
              <span className="lg:block hidden font-newsreader italic lg:translate-x-[28rem] lg:max-w-full max-w-[40rem] font-[300]">
                {title.lineTwo}
              </span>

              <span className="lg:hidden block font-newsreader italic lg:translate-x-[28rem] lg:max-w-full max-w-[40rem] font-[200]">
                for Those
                <span className="translate-x-[25%] block">Who Build</span>
              </span>
              <span className="block font-newsreader italic lg:translate-x-[20rem] font-[200]">
                {title.lineThree}
              </span>
            </h2>
          </motion.div>

          <motion.div className="lg:w-[62.75rem] text-[#f2f0ef] flex flex-col gap-[2rem]">
            {description && (
              <PortableText
                value={description}
                components={richTextComponent}
              />
            )}
            {webButton && <CTAButton className="hidden lg:block" data={webButton} />}
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 3, ease: [0.25, 1, 0.5, 1] }}
          className="lg:w-[167rem] h-[53rem] lg:h-[95.625rem] overflow-clip"
        >
          {image && (
            <GroqImage
              quality={90}
              className="!w-full !h-full !object-cover"
              src={image}
              alt={image?.name}
            />
          )}
        </motion.div>
      </div>
    </section>
  );
}
